import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Col,
  Divider,
  Input,
  notification,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  TablePaginationConfig,
  Typography,
} from "antd";
import CreateInvoiceModal from "./CreateInvoice";
import queryString from "query-string";
import { PlusCircleOutlined } from "@ant-design/icons";
import InvoiceStatusComponent, {
  InvoiceStatus,
} from "components/common/InvoiceStatus";
import { TableActions } from "components/common";
import dayjs from "dayjs";
import {
  useSalesServiceCancelInvoice,
  useSalesServiceGetInvoices,
  useSalesServiceGetInvoicesKey,
} from "api/queries";
import { useNavigate } from "react-router-dom";
import useFilters, { FilterTemplate, FilterType } from "hooks/UseFilter";
import { useAbility } from "@casl/react";
import { AbilityContext } from "utils/abilit";
import { ExtraPermissionType, PermissionType, SubjectKey } from "utils/enums";
import useAuth from "hooks/UseAuth";

export default function InvoiceListing() {
  const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [sortQuery, setSortQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [dateRangePreset, setDateRangePreset] = useState("custom");
  const [presetDateRange, setPresetDateRange] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({ startDate: null, endDate: null });

  const queryClient = useQueryClient();
  const authContext = useAuth();
  const nav = useNavigate();

  const ability = useAbility(AbilityContext);

  const filtersDef: any[] = [
    {
      key: "invoiceNumber",
      label: "Invoice Number",
      type: FilterType.Text,
    },
    {
      key: "invoiceStatus",
      label: "Invoice Status",
      type: FilterType.MultiSelect,
      options: [
        { label: "Draft", value: "DRAFT" },
        { label: "Sent", value: "SENT" },
        { label: "Paid", value: "PAID" },
        { label: "Partial", value: "PARTIAL" },
        { label: "Unpaid", value: "UNPAID" },
        { label: "Over Due", value: "OVER_DUE" },
      ],
    },
    //date range for issue date
    {
      key: "invoiceDate",
      label: "Issue Date",
      type: FilterType.DateRange,
    },
    {
      key: "invoiceDueDate",
      label: "Due Date",
      type: FilterType.DateRange,
    },
    {
      key: "billingAddress",
      label: "Billing Address",
      type: FilterType.Text,
    },
    {
      key: "customer",
      label: "Customer",
      type: FilterType.template,
      template: FilterTemplate.CustomerSelect,
    },
    {
      key: "location",
      label: "Location",
      type: FilterType.template,
      template: FilterTemplate.LocationSelect,
    },
    ...(ability.can(ExtraPermissionType.MANAGE, SubjectKey.INVOICE)
      ? [
          {
            key: "createdBy",
            label: "Employee",
            type: FilterType.template,
            template: FilterTemplate.UserSelect,
          },
        ]
      : []),
  ];

  const { mutate: cancelInvoiceMutation, isPending: isDeletePending } =
    useSalesServiceCancelInvoice({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useSalesServiceGetInvoicesKey],
        });
      },
      onError: (error: any) => {
        notification.error({
          description:
            error?.body?.message ||
            error?.response?.data?.message ||
            error?.message,
          type: "error",
          message: "Failed to cancel invoice",
        });
      },
    });

  const { filtersQuery, FilterButton, Filters } = useFilters(filtersDef);

  // Add hidden createdBy filter if user doesn't have MANAGE permission
  const finalFiltersQuery = useMemo(() => {
    const baseQuery = {
      ...filtersQuery,
      ...(ability.can(ExtraPermissionType.MANAGE, SubjectKey.INVOICE)
        ? {}
        : currentUserId
        ? { createdBy: currentUserId }
        : {}),
    };

    // Add preset date range if not custom
    if (presetDateRange.startDate && presetDateRange.endDate) {
      baseQuery.invoiceDate = {
        $gte: presetDateRange.startDate,
        $lte: presetDateRange.endDate,
      };
    }

    return baseQuery;
  }, [filtersQuery, ability, currentUserId, presetDateRange]);

  // Handle date range preset changes
  const handleDateRangePresetChange = (value: string) => {
    setDateRangePreset(value);

    const now = dayjs();
    let startDate: string | null = null;
    let endDate: string | null = null;

    switch (value) {
      case "today":
        startDate = now.startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        endDate = now.endOf("day").format("YYYY-MM-DDTHH:mm:ss");
        break;
      case "thisWeek":
        startDate = now.startOf("week").format("YYYY-MM-DDTHH:mm:ss");
        endDate = now.endOf("week").format("YYYY-MM-DDTHH:mm:ss");
        break;
      case "thisMonth":
        startDate = now.startOf("month").format("YYYY-MM-DDTHH:mm:ss");
        endDate = now.endOf("month").format("YYYY-MM-DDTHH:mm:ss");
        break;
      case "thisYear":
        startDate = now.startOf("year").format("YYYY-MM-DDTHH:mm:ss");
        endDate = now.endOf("year").format("YYYY-MM-DDTHH:mm:ss");
        break;
      case "custom":
      default:
        startDate = null;
        endDate = null;
        break;
    }

    setPresetDateRange({ startDate, endDate });
  };

  const dateRangeOptions = [
    { label: "Custom", value: "custom" },
    { label: "Today", value: "today" },
    { label: "This Week", value: "thisWeek" },
    { label: "This Month", value: "thisMonth" },
    { label: "This Year", value: "thisYear" },
  ];

  const { data, isLoading, isPending, isFetching, refetch } =
    useSalesServiceGetInvoices({
      filters: finalFiltersQuery,
      pagination: queryString.stringify({
        offset: (page - 1) * limit,
        limit,
      }),
      sort: sortQuery,
      searchQuery,
    });

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: "auto",
      sorter: true,
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: "auto",
      render: (customer: any) => customer?.customerName || "-",
      sorter: true,
    },
    {
      title: "Due Date",
      dataIndex: "invoiceDueDate",
      key: "invoiceDueDate",
      width: "auto",
      render: (date: string) => dayjs(date).format("DD/MM/YYYY"),
      sorter: true,
    },
    {
      title: "Issue Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: "auto",
      render: (date: string) => dayjs(date).format("DD/MM/YYYY"),
      sorter: true,
    },
    {
      title: "Billing Address",
      dataIndex: "billingAddress",
      key: "billingAddress",
      width: "auto",
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "invoiceStatus",
      key: "invoiceStatus",
      width: "auto",
      render: (status: string) => (
        <InvoiceStatusComponent status={status as InvoiceStatus} />
      ),
      sorter: true,
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: "auto",
      render: (paymentMethod: any) => paymentMethod?.paymentMethodName || "-",
      sorter: true,
    },
    {
      title: "Total Amount",
      dataIndex: "invoiceTotalAmount",
      key: "invoiceTotalAmount",
      width: "auto",
      render: (value: number) => `${value?.toLocaleString?.() ?? value}`,
      sorter: true,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: "auto",
      render: (location: any) => location?.locationName || "-",
    },
    {
      title: "Creation Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: "auto",
      render: (date: string) => dayjs(date).format("DD/MM/YY HH:mm"),
      sorter: true,
    },
    {
      title: "Actions",
      dataIndex: "action",
      key: "action",
      render: (_r: any, record: any) => (
        <TableActions
          onView={() => nav(`/sales/invoices/${record.idInvoice}`)}
          onDeletePopConfirm={
            record?.invoiceStatus === InvoiceStatus.SENT &&
            ability.can(PermissionType.DELETE, SubjectKey.INVOICE)
              ? () => handleDeleteInvoice(record.idInvoice)
              : undefined
          }
          onDeletePopConfirmMessage={
            record?.invoiceStatus === InvoiceStatus.SENT &&
            ability.can(PermissionType.DELETE, SubjectKey.INVOICE)
              ? "Are you sure?"
              : undefined
          }
          onDeletePopConfirmDescription="This invoice will be cancel permanently"
          onDeleteLoading={isDeletePending}
        />
      ),
    },
  ];

  const handleDeleteInvoice = async (id: string) => {
    cancelInvoiceMutation({
      idInvoice: +id,
    });
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: any,
    sorter: any
  ) => {
    if (pagination) {
      setPage(pagination.current || 1);
      setLimit(pagination.pageSize || 10);
    }

    if (sorter && Object.keys(sorter).length > 0) {
      const s: any = {};
      s[sorter.field] = sorter.order === "ascend" ? "asc" : "desc";
      setSortQuery(s);
    }
  };

  useEffect(() => {
    refetch();
  }, [sortQuery, limit, page, searchQuery, finalFiltersQuery, refetch]);

  useEffect(() => {
    if (authContext?.userDetails && authContext?.userDetails !== "") {
      const userDetails: any = JSON.parse(authContext.userDetails);
      console.log({ userDetails });
      setCurrentUserId(userDetails.idUser);
    }
  }, [authContext.userDetails]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 20,
          flexWrap: "wrap",
          display: ability.can(
            ExtraPermissionType.READ_KPIS,
            SubjectKey.INVOICE
          )
            ? "flex"
            : "none",
        }}
        justify="start"
      >
        <Col flex="1 1 180px" style={{ minWidth: 180, maxWidth: 220 }}>
          <Card>
            <Statistic
              title="Total Invoices"
              value={data?.totalInvoices || 0}
              loading={isLoading || isPending || isFetching}
              valueStyle={{ display: "flex", alignItems: "center", gap: 8 }}
            />
          </Card>
        </Col>
        <Col flex="1 1 180px" style={{ minWidth: 180, maxWidth: 220 }}>
          <Card>
            <Statistic
              title="Total Amount"
              value={Number(data?.totalInvoicesAmount || 0).toLocaleString(
                undefined,
                { minimumFractionDigits: 2, maximumFractionDigits: 2 }
              )}
              loading={isLoading || isPending || isFetching}
              valueStyle={{ display: "flex", alignItems: "center", gap: 8 }}
            />
          </Card>
        </Col>
        <Col flex="1 1 180px" style={{ minWidth: 180, maxWidth: 220 }}>
          <Card>
            <Statistic
              title="Partial Paid"
              value={data?.partialPaidCount || 0}
              loading={isLoading || isPending || isFetching}
              valueStyle={{ display: "flex", alignItems: "center", gap: 8 }}
            />
          </Card>
        </Col>
        <Col flex="1 1 180px" style={{ minWidth: 180, maxWidth: 220 }}>
          <Card>
            <Statistic
              title="Canceled"
              value={data?.canceledCount || 0}
              loading={isLoading || isPending || isFetching}
              valueStyle={{ display: "flex", alignItems: "center", gap: 8 }}
            />
          </Card>
        </Col>
        <Col flex="1 1 180px" style={{ minWidth: 180, maxWidth: 220 }}>
          <Card>
            <Statistic
              title="Paid"
              value={data?.paidCount || 0}
              loading={isLoading || isPending || isFetching}
              valueStyle={{ display: "flex", alignItems: "center", gap: 8 }}
            />
          </Card>
        </Col>
        <Col flex="1 1 180px" style={{ minWidth: 180, maxWidth: 220 }}>
          <Card>
            <Statistic
              title="Not Paid"
              value={data?.notPaidCount || 0}
              loading={isLoading || isPending || isFetching}
              valueStyle={{ display: "flex", alignItems: "center", gap: 8 }}
            />
          </Card>
        </Col>
        <Col flex="1 1 180px" style={{ minWidth: 180, maxWidth: 220 }}>
          <Card>
            <Statistic
              title="Total Amount Paid"
              value={data?.totalAmountPaid || 0}
              loading={isLoading || isPending || isFetching}
              valueStyle={{ display: "flex", alignItems: "center", gap: 8 }}
            />
          </Card>
        </Col>
        <Col flex="1 1 180px" style={{ minWidth: 180, maxWidth: 220 }}>
          <Card>
            <Statistic
              title="Total Amount Not Paid"
              value={data?.totalAmountNotPaid || 0}
              loading={isLoading || isPending || isFetching}
              valueStyle={{ display: "flex", alignItems: "center", gap: 8 }}
            />
          </Card>
        </Col>

        {data?.paymentMethods &&
          data?.paymentMethods.length > 0 &&
          data?.paymentMethods.map((method: any) => {
            return (
              <Col
                flex="1 1 180px"
                style={{ minWidth: 180, maxWidth: 220 }}
                key={method.name}
              >
                <Card>
                  <Statistic
                    title={`Total ${method.name}`}
                    value={method.amount || 0}
                    loading={isLoading || isPending || isFetching}
                    valueStyle={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  />
                </Card>
              </Col>
            );
          })}
      </Row>
      <Row>
        <Col xs={12}>
          <Typography.Title>Invoices</Typography.Title>
        </Col>
        <Col
          xs={12}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Space>
            {FilterButton}
            <Button
              icon={<PlusCircleOutlined />}
              type="primary"
              onClick={() => setCreateInvoiceModalOpen(true)}
              disabled={!ability.can(PermissionType.CREATE, SubjectKey.INVOICE)}
            >
              Add new
            </Button>
          </Space>
        </Col>

        <Col xs={24}>{Filters}</Col>

        <Col xs={24}>
          <Divider style={{ opacity: 0.4 }} />
        </Col>

        <Col xs={24} style={{ marginBottom: 15 }}>
          <Space.Compact style={{ width: "100%" }}>
            <Input.Search
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
              style={{ flex: 1 }}
            />
            <Select
              value={dateRangePreset}
              onChange={handleDateRangePresetChange}
              options={dateRangeOptions}
              style={{ width: 150 }}
              placeholder="Date Range"
            />
          </Space.Compact>
        </Col>

        <Col xs={24}>
          <Table
            scroll={{ x: "max-content" }}
            loading={isLoading || isPending || isFetching}
            columns={columns}
            dataSource={data?.items}
            pagination={{
              total: data?.total,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ["10", "20", "30", "40", "50"],
            }}
            onChange={handleTableChange}
            rowKey={(item) => item.idInvoice}
          />
        </Col>
      </Row>

      <CreateInvoiceModal
        isOpen={createInvoiceModalOpen}
        onClose={() => setCreateInvoiceModalOpen(false)}
        queryClient={queryClient}
      />
    </motion.div>
  );
}
